"use client";
import { LegalMenu } from "./LegalMenu/LegalMenu";
import { MenuType } from "../../types/baseTypes";
import { customSanitize } from "../../utils/customSanitize";
import {
  optionsFooterColumnsType,
  OptionsFooterType,
  OptionsSocialMediaType,
} from "@/types/OptionType";
import styles from "./Footer.module.scss";
import { Contact } from "./Contact/Contact";
import { Socials } from "./Socials/Socials";
import FooterMenu from "./FooterMenu/FooterMenu";
import { ConsentManagement } from "@aleksundshantu/aunds-cookie-plugin-react";
import { passACFLink } from "@/utils/passACFLink";
import { Section } from "../Section/Section";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { useRouter } from "next/router";
import { CustomLink } from "../CustomLink";
import { MediaItem } from "../MediaItem/MediaItem";

// TODO and only pass props that are actually used not whole options object and not whoel menu object
interface FooterProps {
  legalMenu?: MenuType;
  footerMenu?: MenuType;
  autoShowConsentManagement: boolean;
  footerOptions: OptionsFooterType;
  socialMedia: OptionsSocialMediaType;
}

export const Footer = ({
  legalMenu,
  footerMenu,
  socialMedia,
  footerOptions,
  autoShowConsentManagement,
}: FooterProps) => {
  return (
    <DesktopFooter
      legalMenu={legalMenu}
      footerMenu={footerMenu}
      socialMedia={socialMedia}
      footerOptions={footerOptions}
      autoShowConsentManagement={autoShowConsentManagement}
    />
  );
};

const FooterColumn = ({
  type,
  columnData,
  footerMenu,
  socialMedia,
}: {
  type:
    | "logos"
    | "contact"
    | "navigation-links"
    | "social-icons"
    | "social-icons-with-links";
  columnData: optionsFooterColumnsType;
  footerMenu: MenuType | undefined;
  socialMedia: OptionsSocialMediaType;
}) => {
  switch (type) {
    case "logos": {
      return (
        <div className={`${styles["footer-logos"]}`}>
          {Array.isArray(columnData.logos_footer) &&
            columnData.logos_footer.map((el, i) => {
              const { logo, link } = el;
              return link && link.url ? (
                <CustomLink
                  key={"footer-logo-" + i}
                  className={`${styles["footer-logo"]}`}
                  {...passACFLink(el, "link")}
                >
                  <MediaItem
                    image_desktop={logo}
                    sizes="(max-width: 1023px) 33vw, 200w"
                    className={styles["logo"]}
                    objectFit="contain"
                  />
                </CustomLink>
              ) : (
                <div
                  key={"footer-logo-" + i}
                  className={`${styles["footer-logo"]}`}
                >
                  <MediaItem
                    image_desktop={logo}
                    sizes="(max-width: 1023px) 33vw, 200w"
                    className={styles["logo"]}
                    objectFit="contain"
                  />
                </div>
              );
            })}
        </div>
      );
    }
    case "contact": {
      return (
        <div className={`${styles["footer-contact"]}`}>
          <Contact
            contact={{
              title: columnData.contact.title,
              content: columnData.contact.content,
            }}
          />
        </div>
      );
    }
    case "navigation-links": {
      return (
        <div className={`${styles["footer-menu"]}`}>
          <FooterMenu
            menu={footerMenu}
            headline={columnData.navigations_links_title}
          />
        </div>
      );
    }
    case "social-icons": {
      return (
        <div className={`${styles["footer-socials"]}`}>
          <Socials
            platforms={socialMedia.platforms}
            headline={columnData.social_links_title}
          />
        </div>
      );
    }
    case "social-icons-with-links": {
      return (
        <div className={`${styles["footer-socials"]}`}>
          <Socials
            platforms={socialMedia.platforms}
            headline={columnData.social_links_title}
            showName
          />
        </div>
      );
    }
    default: {
      return <></>;
    }
  }
};

const DesktopFooter = ({
  legalMenu,
  footerMenu,
  socialMedia,
  footerOptions,
  autoShowConsentManagement,
}: FooterProps) => {
  const { getLabel } = useGlobalContext();
  const router = useRouter();

  const {
    webdesign,
    navigation,
    column_1,
    column_2,
    column_3,
    column_4,
    footer_structure,
  } = footerOptions || {};

  function calculateFooterColumnWidth() {
    let numberColumns = 0;
    if (footer_structure?.column_1?.show_column) numberColumns++;
    if (footer_structure?.column_2?.show_column) numberColumns++;
    if (footer_structure?.column_3?.show_column) numberColumns++;
    if (footer_structure?.column_4?.show_column) numberColumns++;
    if (numberColumns === 4) return "25%";
    if (numberColumns === 3) return "33.33%";
    if (numberColumns === 2) return "50%";
    if (numberColumns === 1) return "100%";
    return "100%";
  }

  return (
    <footer
      className={`footer is-clipped ${styles.footer} ${navigation.footer_menu.show_line ? styles["footer-with-line"] : ""}`}
      aria-label="footer"
      id="kontakt"
      style={
        {
          "--footer-column-width": `${calculateFooterColumnWidth()}`,
        } as React.CSSProperties
      }
    >
      <ConsentManagement
        wpApiUrl={process.env.WP_API_URL!}
        autoShow={autoShowConsentManagement}
        reloadOnSave={true}
        forceShow={false}
        path={undefined}
        locale={
          router.locale !== router.defaultLocale ? router.locale : undefined
        }
        cookieExpirationTimeMs={undefined}
      />
      <div className="container">
        <div className={`${styles["footer-top"]}`}>
          {footer_structure?.column_1?.show_column && column_1 && (
            <FooterColumn
              type={footer_structure.column_1.type}
              columnData={column_1}
              footerMenu={footerMenu}
              socialMedia={socialMedia}
            />
          )}
          {footer_structure?.column_2?.show_column && column_2 && (
            <FooterColumn
              type={footer_structure.column_2.type}
              columnData={column_2}
              footerMenu={footerMenu}
              socialMedia={socialMedia}
            />
          )}
          {footer_structure?.column_3?.show_column && column_3 && (
            <FooterColumn
              type={footer_structure.column_3.type}
              columnData={column_3}
              footerMenu={footerMenu}
              socialMedia={socialMedia}
            />
          )}
          {footer_structure?.column_4?.show_column && column_4 && (
            <FooterColumn
              type={footer_structure.column_4.type}
              columnData={column_4}
              footerMenu={footerMenu}
              socialMedia={socialMedia}
            />
          )}
        </div>

        <div className={`${styles["footer-bottom"]}`}>
          {legalMenu && <LegalMenu menu={legalMenu} />}
          <div
            className={`${styles["webdesign"]} has-text-centered developed`}
            dangerouslySetInnerHTML={{ __html: customSanitize(webdesign) }}
          />
        </div>
      </div>

      <Section
        tag="div"
        disableColumns
        className="backtotop"
        containerClassName="is-flex is-justify-content-flex-end"
      >
        {/* eslint-disable-next-line react/forbid-elements */}
        <a href="#pagestart" aria-label={getLabel("aria_label_back_to_top")}>
          <i className="arrow up"></i>
          {getLabel("back_to_top", "Back to top")}
        </a>
      </Section>
    </footer>
  );
};
